// src/components/Cycling/PlannedVsCompletedCycling.js
import React, { useEffect, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import { useTranslation } from '../../i18n';

/* ---------- helpers ---------- */
const pad2 = (n) => String(n).padStart(2, '0');

const normalizeDurationForPlanned = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(s)) {
    const [h, m, sec = '00'] = s.split(':');
    return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
  }
  let total = 0, m;
  const re = /(\d+(?:\.\d+)?)\s*(h|hr|hora|horas|m|min|minutos|s|seg|segundos)/gi;
  while ((m = re.exec(s))) {
    const val = parseFloat(m[1]);
    const u = m[2].toLowerCase();
    if (['h','hr','hora','horas'].includes(u)) total += val * 3600;
    else if (['m','min','minutos'].includes(u)) total += val * 60;
    else total += val;
  }
  if (total) {
    const secs = Math.round(total);
    const hh = Math.floor(secs / 3600);
    const mm = Math.floor((secs % 3600) / 60);
    const ss = secs % 60;
    return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
  }
  if (/^\d+$/.test(s)) {
    if (s.length <= 2) return `${pad2(s)}:00:00`;
    if (s.length <= 4) {
      const mins = parseInt(s, 10);
      return `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}:00`;
    }
    const secs = parseInt(s, 10);
    const hh = Math.floor(secs / 3600);
    const mm = Math.floor((secs % 3600) / 60);
    const ss = secs % 60;
    return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
  }
  return s;
};

const displayCompletedDuration = (val) => {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(s)) return s;
  const num = Number(s);
  if (!Number.isFinite(num)) return s;
  const secs = Math.max(0, Math.round(num));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const sec = secs % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
};

const isZeroHHMMSS = (v) => /^0{1,2}:0{2}(?::0{2})?$/.test(String(v ?? '').trim());
const toBlankDuration  = (v) => { const s = String(v ?? '').trim(); if (!s) return ''; return isZeroHHMMSS(s) ? '' : s; };
const toBlankDistance  = (v) => { const s = String(v ?? '').trim(); if (s === '') return ''; const n = Number(s.replace(',', '.')); return !Number.isFinite(n) || n === 0 ? '' : s; };

const PlannedVsCompletedCycling = ({ planned = {}, completed = {}, setPlanned }) => {
  const { t } = useTranslation();

  const fieldMapping = {
    duracion:          completed.duration          ?? completed.duracion          ?? '',
    distancia:         completed.distance          ?? completed.distancia         ?? '',
    ritmo_medio:       completed.avgPace           ?? completed.ritmo_medio       ?? '',
    calorias:          completed.calories          ?? completed.calorias          ?? '',
    desnivel_positivo: completed.totalAscent       ?? completed.desnivel_positivo ?? '',
    tte: completed.tte ?? '',
    tea: completed.tea ?? '',
    tss: completed.tss ?? '',
  };

  const fields = [
    { field: 'duracion',          label: t('cycling.duration'),      unit: 'hh:mm:ss' },
    { field: 'distancia',         label: t('cycling.distance'),      unit: 'km'       },
    { field: 'ritmo_medio',       label: t('cycling.avgPace'),       unit: 'min/km'   },
    { field: 'calorias',          label: t('cycling.calories'),      unit: 'kcal'     },
    { field: 'desnivel_positivo', label: t('cycling.elevationGain'), unit: 'm'        },
    { field: 'tte',               label: 'TTE',                      unit: ''         },
    { field: 'tea',               label: 'TEA',                      unit: ''         },
    { field: 'tss',               label: 'TSS',                      unit: ''         },
  ];

  const [draft, setDraft] = useState({
    duracion:  toBlankDuration(planned.duracion  ?? planned.duration),
    distancia: toBlankDistance(planned.distancia ?? planned.distance),
  });

  useEffect(() => {
    setDraft({
      duracion:  toBlankDuration(planned.duracion  ?? planned.duration),
      distancia: toBlankDistance(planned.distancia ?? planned.distance),
    });
  }, [planned?.duracion, planned?.duration, planned?.distancia, planned?.distance]);

  const commitDuration = () => {
    const normalized = normalizeDurationForPlanned(draft.duracion);
    setDraft((d) => ({ ...d, duracion: normalized || '' }));
    setPlanned({ ...planned, duracion: normalized || '' });
  };

  const commitDistance = () => {
    const v   = String(draft.distancia ?? '').replace(',', '.').trim();
    const n   = Number(v);
    const out = !v || !Number.isFinite(n) || n === 0 ? '' : v;
    setDraft((d) => ({ ...d, distancia: out }));
    setPlanned({ ...planned, distancia: out });
  };

  return (
    <div>
      <h5 className="text-success mb-3">{t('cycling.plannedVsCompleted')}</h5>
      <Table striped bordered hover>
        <thead style={{ backgroundColor: '#193255', color: '#fff' }}>
          <tr>
            <th>{t('cycling.metric')}</th>
            <th>{t('cycling.planned')}</th>
            <th>{t('cycling.completed')}</th>
            <th>{t('cycling.unit')}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(({ field, label, unit }) => {
            const isDur  = field === 'duracion';
            const isDist = field === 'distancia';
            return (
              <tr key={field}>
                <td>{label}</td>
                <td>
                  {isDur ? (
                    <Form.Control
                      type="text"
                      placeholder="hh:mm:ss"
                      value={draft.duracion ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, duracion: e.target.value }))}
                      onBlur={commitDuration}
                    />
                  ) : isDist ? (
                    <Form.Control
                      type="text"
                      value={draft.distancia ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, distancia: e.target.value }))}
                      onBlur={commitDistance}
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      value={planned?.[field] ?? ''}
                      onChange={(e) => setPlanned({ ...planned, [field]: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  <Form.Control
                    type="text"
                    value={isDur ? displayCompletedDuration(fieldMapping[field]) : (fieldMapping[field] ?? '')}
                    readOnly
                  />
                </td>
                <td>{unit}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default PlannedVsCompletedCycling;
